<template>
	<div
		class="ao-great-select-list grid-layout fill-height"
		:class="{ 'mobile-layout': isMobile }"
	>
		<!-- 1. Панель управления (поиск, "выбрать все" и т.д.) -->
		<div
			v-if="isSearch || isShowFullListToggleAvailable"
			class="control-panel d-flex align-center px-3 pb-2"
		>
			<AoTextField
				v-if="isSearch"
				:model-value="searchQuery"
				hide-details
				:autofocus="!isMobile"
				color="primary"
				:placeholder="$t('Search')"
				density="compact"
				variant="underlined"
				:clearable="false"
				@update:modelValue="$emit('update:searchQuery', $event)"
			/>

			<AoIconBtn
				v-if="isShowFullListToggleAvailable"
				class="ml-4"
				size="small"
				:iconName="showFullList ? 'collapseClose' : 'collapseOpen'"
				@click="$emit('click:toggleShowFullList')"
			/>
		</div>

		<!-- 2. Состояние загрузки -->
		<div
			v-if="loading"
			class="list-container px-2"
		>
			<v-skeleton-loader
				v-for="n in 3"
				:key="n"
				type="list-item"
				class="mb-0"
			/>
		</div>

		<!-- 3. Отображение списка -->
		<template v-else-if="items.length > 0">
			<div class="list-container d-flex flex-column">
				<div
					v-if="multiselect && showSelectAll"
					class="ml-4"
				>
					<AoCheckbox
						:model-value="isAllSelected"
						:label="isAllSelected ? $t('Clear selection') : $t('Select all')"
						@update:modelValue="$emit('click:toggleSelectAll')"
					/>
				</div>
				<v-virtual-scroll
					:items="items"
					:max-height="isMobile ? undefined : 240"
					:itemKey="itemValue"
					@scroll="onScroll"
				>
					<template #default="{ item }">
						<div
							v-ripple
							class="select-list-item d-flex cursor-pointer"
							:class="{ 'active': isItemSelected(item) }"
							@click="$emit('click:item', item)"
						>
							<div
								v-if="multiselect"
								class="item-checkbox-wrap flex-0-0 d-flex"
							>
								<AoCheckbox
									:modelValue="isItemSelected(item)"
									readonly
									class="ma-auto"
									isLabelMultiRow
									identRight
									:isNarrow="false"
									@update:modelValue="$emit('click:item', item)"
								/>
							</div>
							<v-list-item
								density="compact"
								:ripple="false"
								class="flex-1-1"
							>
								<template
									v-if="prependAvatar || prependIcon"
									#prepend
								>
									<AoAvatar
										v-if="prependAvatar"
										:image="item[prependAvatar]"
										:size="32"
									/>
									<AoIcon
										v-if="prependIcon"
										:iconName="item[prependIcon]"
										size="large"
										class="me-2"
									/>
								</template>

								<template
									v-if="appendAvatar || appendIcon"
									#append
								>
									<AoAvatar
										v-if="appendAvatar"
										:image="item[appendAvatar]"
										:size="32"
									/>
									<AoIcon
										v-if="appendIcon"
										:iconName="item[appendIcon]"
										size="large"
										class="ms-2"
									/>
								</template>

								<v-list-item-title
									class="ao-c1"
								>
									{{ item[itemTitle] }}
								</v-list-item-title>

								<v-list-item-subtitle
									v-if="itemSubtitle"
								>
									{{ item[itemSubtitle] }}
								</v-list-item-subtitle>

								<v-list-item-subtitle
									v-if="itemText"
								>
									{{ item[itemText] }}
								</v-list-item-subtitle>

							</v-list-item>
						</div>
					</template>
				</v-virtual-scroll>
			</div>
		</template>

		<!-- 4. Состояние "Ничего не найдено" / "Пусто" -->
		<div
			v-else
			class="list-container text-center text-disabled py-4"
		>
			<p>{{ $t('No data') }}</p>
		</div>
	</div>
</template>

<script setup>
import { computed } from 'vue'

import AoTextField from '../AoTextField.vue'
import AoCheckbox from '../AoCheckbox.vue'
import AoIconBtn from '../AoIconBtn.vue'
import AoAvatar from '../AoAvatar.vue'
import AoIcon from '../AoIcon.vue'

// 1. Определяем API компонента
const props = defineProps({
	items: { type: Array, required: true },
	selectedItems: { type: [Array, Object], default: null },
	loading: { type: Boolean, default: false },
	isSearch: { type: Boolean, default: false },
	showSelectAll: { type: Boolean, default: false },
	multiselect: { type: Boolean, default: false },
	searchQuery: { type: String, default: '' },
	itemValue: { type: String, required: true },
	itemTitle: { type: String, required: true },
	itemSubtitle: { type: String, default: null },
	itemText: { type: String, default: null },
	isAllSelected: { type: Boolean, default: false },
	showFullList: { type: Boolean, default: false },
	isShowFullListToggleAvailable: { type: Boolean, default: false },
	isMobile: { type: Boolean, default: false },
	prependAvatar: { type: String, default: null },
	prependIcon: { type: String, default: null },
	appendAvatar: { type: String, default: null },
	appendIcon: { type: String, default: null },
})

const emit = defineEmits({
	'update:searchQuery': null,
	'click:toggleSelectAll': null,
	'click:toggleShowFullList': null,
	'click:item': null,
	'update:scrolledToTop': null,
})

let lastScrolledToTopState = true

const onScroll = (event) => {
  const isAtTop = event.target.scrollTop === 0
  // Эмитим событие только если состояние изменилось,
  // чтобы избежать лишних перерисовок.
  if (isAtTop !== lastScrolledToTopState) {
    lastScrolledToTopState = isAtTop
    emit('update:scrolledToTop', isAtTop)
  }
}

// 2. Внутренняя логика для отображения
// Создаем Set из выбранных значений для быстрой проверки O(1)
const selectedValuesSet = computed(() => {
	if (!props.selectedItems) return new Set()
	const items = Array.isArray(props.selectedItems) ? props.selectedItems : [props.selectedItems]
	return new Set(items.map(item => item[props.itemValue]))
})

// Метод для проверки, выбран ли элемент. Использует быстрый Set.
function isItemSelected(item) {
	return selectedValuesSet.value.has(item[props.itemValue])
}
</script>

<style lang="scss">
.ao-great-select-list {

	// Переписываем стили с Flexbox на Grid
	&.grid-layout {
		display: grid;
		// Определяем две строки:
		// 1. `auto`: высота панели управления будет определяться ее содержимым.
		// 2. `1fr`: контейнер списка займет ВСЁ оставшееся доступное пространство.
		grid-template-rows: auto 1fr;
		// Это ключевое свойство, которое предотвращает "выталкивание" контента.
		overflow: hidden;

		.control-panel {
			// Эта строка определяет, что данный элемент относится к первой строке грида.
			// (Технически, это можно опустить, так как это первый элемент в DOM,
			// но для ясности лучше оставить).
			grid-row: 1;
		}

		.list-container {
			// Все контейнеры для списка (загрузка, сам список, "нет данных")
			// относятся ко второй строке грида.
			grid-row: 2;
			// Важно: контейнер списка должен уметь скроллиться сам, если его
			// внутреннее содержимое (например, чекбокс) не помещается.
			overflow: auto;
		}

		&.mobile-layout {
			// Переопределяем порядок строк
			grid-template-rows: 1fr auto; // Список сверху, поиск снизу

			.control-panel {
				grid-row: 2; // Панель управления теперь во второй строке
				border-top: 1px solid #eee; // Добавляем разделитель для красоты
				padding-top: 8px;
			}

			.list-container {
				grid-row: 1; // Контейнер списка теперь в первой строке
			}

		}

	}

	.select-list-item {

		&.active {
			background-color: rgba(var(--v-theme-primary), 0.25) !important;
		}

		// .item-checkbox-wrap {

		// }

	}

}
</style>
